import { camelizeKeys } from 'humps'
import { Commit } from 'vuex'

import ChangeDefaultPasswordForm from '@/models/change-default-password-form'
import ChangePasswordForm from '@/models/change-password-form'
import Customer from '@/models/customer'
import ErrorEntities from '@/models/error-entities'

import axios, { AxiosModule } from '@/axios-module'
import notification from '@/notification'
import { BookingsResponse } from '@/schemas/booking'
import deepmerge from '@/services/deepmerge/deepmerge'
import { AuthMutationTypes } from '@/store/modules/auth/AuthMutationTypes'
import { BookingsMutationTypes } from '@/store/modules/bookings'

import { InvitePageMutationTypes } from './invitePage'
import { MessagesMutationTypes } from './messages'
import { UtilityMutationTypes } from './utility'

export enum CustomerActionTypes {
  GetCurrent = '[Customer] GetCurrent',
  UpdateProfile = '[Customer] UpdateProfile',
  UpdateEmergencyContact = '[Customer] UpdateEmergencyContact',
  ChangePassword = '[Customer] ChangePassword',
  ChangeDefaultPassword = '[Customer] ChangeDefaultPassword',
  UpdateAvatar = '[Customer] UpdateAvatar',
  ManageSubscription = '[Customer] ManageSubscription',
}

export enum CustomerMutationTypes {
  GetSuccess = '[Customer] GetSuccess',
  GetCurrentSuccess = '[Customer] GetCurrentSuccess',
  GetCurrentFailure = '[Customer] GetCurrentFailure',
  UpdateProfile = '[Customer] UpdateProfile',
  UpdateEmergencyContact = '[Customer] UpdateEmergencyContact',
  UpdateProfileSuccess = '[Customer] UpdateProfileSuccess',
  UpdateProfileFailure = '[Customer] UpdateProfileFailure',
  ChangePasswordSuccess = '[Customer] ChangePasswordSuccess',
  ChangePasswordFailure = '[Customer] ChangePasswordFailure',
  ChangeDefaultPasswordSuccess = '[Customer] ChangeDefaultPasswordSuccess',
  ChangeDefaultPasswordFailure = '[Customer] ChangeDefaultPasswordFailure',
  UpdateAvatarSuccess = '[Customer] UpdateAvatarSuccess',
  UpdateAvatarError = '[Customer] UpdateAvatarError',
  DisconnectFacebookSuccess = '[Customer] DisconnectFacebookSuccess',
  ManageSubscriptionSuccess = '[Customer] ManageSubscriptionSuccess',
}

export interface State {
  customerIds: number[]
  customerEntities: { [id: number]: Customer }
  currentCustomerId: number | null
  avatarWasUploaded: boolean
  errors: ErrorEntities
}

const initialState: State = {
  customerIds: [],
  customerEntities: {},
  currentCustomerId: null,
  avatarWasUploaded: false,
  errors: {},
}

export default {
  state: initialState,
  getters: {
    isNewAccount(state: State, getters: any): boolean {
      return getters.getCurrentCustomer
        ? getters.getCurrentCustomer.isNewAccount
        : false
    },
    passwordResetRequired(state: State, getters: any): boolean {
      return getters.getCurrentCustomer
        ? getters.getCurrentCustomer.passwordResetRequired
        : false
    },
    getCustomerById(state: State): (id: number) => Customer | null {
      return (id: number) => state.customerEntities[id]
    },
    getCurrentCustomer(state: State, getters: any): Customer | null {
      return getters.getCustomerById(state.currentCustomerId)
    },
  },
  actions: {
    async [CustomerActionTypes.GetCurrent]({ commit }: { commit: Commit }) {
      try {
        const response = await axios.get('/customer/me')
        commit(UtilityMutationTypes.SetCurrentDate, response.headers.date)
        return commit(CustomerMutationTypes.GetCurrentSuccess, response.data)
      } catch ({ response }) {
        return commit(CustomerMutationTypes.GetCurrentFailure)
      }
    },
    async [CustomerActionTypes.UpdateProfile](
      { commit, getters }: { commit: Commit; state: State; getters: any },
      updates: { [field: string]: any },
    ) {
      const cachedProfile = getters.getCurrentCustomer
      commit(CustomerMutationTypes.UpdateProfile, updates)
      try {
        const response = await axios.put('/customer/me', { customer: updates })
        notification('Account Updated', 'success')
        return commit(CustomerMutationTypes.UpdateProfileSuccess, response.data)
      } catch ({ response }) {
        const message =
          (response && response.data && response.data.error) ||
          'Profile was not updated!'
        notification(message, 'error')
        return commit(CustomerMutationTypes.UpdateProfileFailure, cachedProfile)
      }
    },
    async [CustomerActionTypes.UpdateEmergencyContact](
      { commit, getters }: { commit: Commit; state: State; getters: any },
      updates: { [field: string]: any },
    ) {
      const cachedProfile = getters.getCurrentCustomer
      commit(CustomerMutationTypes.UpdateEmergencyContact, updates)
      try {
        const response = await axios.put('/customer/emergency_contact', updates)
        notification('Emergency Contact updated!', 'success')
        return commit(CustomerMutationTypes.UpdateProfileSuccess, response.data)
      } catch ({ response }) {
        const message =
          response.data.error || 'Emergency Contact was not updated!'
        notification(message, 'error')
        return commit(CustomerMutationTypes.UpdateProfileFailure, cachedProfile)
      }
    },
    async [CustomerActionTypes.ChangeDefaultPassword](
      { commit }: { commit: Commit },
      form: ChangeDefaultPasswordForm,
    ) {
      try {
        await axios.post('/customer/me/change_default_password', {
          customer: form,
        })
        notification('Password updated!', 'success')
        commit(CustomerMutationTypes.ChangeDefaultPasswordSuccess)
      } catch ({ response }) {
        notification('Password was not updated!', 'error')
        commit(
          CustomerMutationTypes.ChangeDefaultPasswordFailure,
          response.data.errors,
        )
      }
    },
    async [CustomerActionTypes.ChangePassword](
      { commit }: { commit: Commit },
      form: ChangePasswordForm,
    ) {
      try {
        await axios.post('/customer/me/change_password', { customer: form })
        notification('Password updated!', 'success')
        commit(CustomerMutationTypes.ChangePasswordSuccess)
      } catch ({ response }) {
        notification('Password was not updated!', 'error')
        commit(
          CustomerMutationTypes.ChangePasswordFailure,
          response.data.errors,
        )
      }
    },
    async [CustomerActionTypes.UpdateAvatar](
      { commit }: { commit: Commit },
      image: File,
    ) {
      try {
        const formData = new FormData()
        formData.append('image', image)

        const response = await AxiosModule.post('/customer/avatar', formData)
        notification('Avatar updated!', 'success')
        commit(CustomerMutationTypes.UpdateAvatarSuccess, response.data)
      } catch ({ response }) {
        notification('Avatar was not updated!', 'error')
        commit(CustomerMutationTypes.UpdateAvatarError, response.data.error)
      }
    },
    async [CustomerActionTypes.ManageSubscription](
      { commit }: { commit: Commit },
      isSubscriptionActive: boolean,
    ) {
      try {
        await axios.post('/customer/me/manage_subscription', {
          isSubscriptionActive,
        })
        notification('Subscription updated!', 'success')
        commit(CustomerMutationTypes.ManageSubscriptionSuccess)
      } catch (e) {
        notification('Error updating subscription', 'error')
      }
    },
  },
  mutations: {
    [CustomerMutationTypes.GetSuccess](state: State, customer: Customer) {
      updateCustomer(state, customer)
    },
    [CustomerMutationTypes.GetCurrentSuccess](
      state: State,
      customer: Customer,
    ) {
      state.customerEntities = deepmerge(state.customerEntities, {
        [customer.id]: customer,
      })
      state.currentCustomerId = customer.id
      state.customerIds = [customer.id]
    },
    [CustomerMutationTypes.GetCurrentFailure](state: State) {
      state.currentCustomerId = null
    },
    [CustomerMutationTypes.UpdateProfile](
      state: State,
      updates: { [field: string]: any },
    ) {
      if (state.currentCustomerId) {
        let customer = state.customerEntities[state.currentCustomerId]
        customer = Object.assign({}, customer, updates)

        state.customerEntities = {
          ...state.customerEntities,
          [customer.id]: customer,
        }
      }
    },
    [CustomerMutationTypes.UpdateProfileSuccess]() {
      return undefined
    },
    [CustomerMutationTypes.UpdateEmergencyContact](
      state: State,
      updates: { [field: string]: any },
    ) {
      if (state.currentCustomerId) {
        let customer = state.customerEntities[state.currentCustomerId]

        const emergencyContact = { ...customer.emergencyContact, ...updates }
        customer = Object.assign({}, customer, { emergencyContact })

        state.customerEntities = {
          ...state.customerEntities,
          [customer.id]: customer,
        }
      }
    },
    [CustomerMutationTypes.UpdateProfileFailure](
      state: State,
      cachedCustomer: Customer,
    ) {
      if (state.currentCustomerId) {
        const customer = state.customerEntities[state.currentCustomerId]
        state.customerEntities = {
          ...state.customerEntities,
          [customer.id]: cachedCustomer,
        }
      }
    },
    [CustomerMutationTypes.UpdateAvatarSuccess](
      state: State,
      customer: Customer,
    ) {
      customer = camelizeKeys(customer) as Customer

      state.customerEntities = {
        ...state.customerEntities,
        [customer.id]: customer,
      }
      state.errors = { ...state.errors, avatar: [] }
    },
    [CustomerMutationTypes.UpdateAvatarError](state: State, error: string) {
      state.errors = {
        ...state.errors,
        avatar: [error],
      }
    },
    [CustomerMutationTypes.ManageSubscriptionSuccess](state: State) {
      if (state.currentCustomerId) {
        let customer = state.customerEntities[state.currentCustomerId]
        customer = {
          ...customer,
          isSubscriptionActive: !customer.isSubscriptionActive,
        }

        state.customerEntities = {
          ...state.customerEntities,
          [customer.id]: customer,
        }
      }
    },
    [CustomerMutationTypes.DisconnectFacebookSuccess](state: State) {
      if (state.currentCustomerId) {
        let customer = state.customerEntities[state.currentCustomerId]
        customer = { ...customer, connectedToFacebook: false }

        state.customerEntities = {
          ...state.customerEntities,
          [customer.id]: customer,
        }
      }
    },
    [AuthMutationTypes.LoginSuccess](state: State, customer: Customer) {
      state.currentCustomerId = customer.id
    },
    [AuthMutationTypes.LoginFailure](state: State) {
      state.currentCustomerId = null
    },
    [AuthMutationTypes.LogoutSuccess](state: State) {
      state.currentCustomerId = null
    },
    [MessagesMutationTypes.LoadByBookingIdSuccess](
      state: State,
      normalizedMessages: any,
    ) {
      updateCustomersFromMessages(normalizedMessages, state)
    },
    [MessagesMutationTypes.UpdateByBookingIdSuccess](
      state: State,
      normalizedMessages: any,
    ) {
      updateCustomersFromMessages(normalizedMessages, state)
    },
    [InvitePageMutationTypes.GetInvitationSuccess](
      state: State,
      invitation: any,
    ) {
      state.customerEntities = Object.assign(
        {},
        state.customerEntities,
        invitation.entities.customers,
      )
    },
    [BookingsMutationTypes.LoadSuccess](
      state: State,
      bookingsResponse: BookingsResponse,
    ) {
      const { customers = {} } = bookingsResponse.entities

      state.customerEntities = deepmerge(state.customerEntities, customers)
      state.customerIds = Object.keys(state.customerEntities).map(Number)
    },
  },
}

function updateCustomersFromMessages(normalizedMessages: any, state: State) {
  const customers = normalizedMessages.entities.customers
  // TODO: refactor this later. This is a wrong usage of normalizr
  if (customers) {
    Object.keys(customers)
      .map(Number)
      .map((customerId: number) => {
        const customer = customers[customerId]
        updateCustomer(state, customer)
      })
  }
}

function updateCustomer(state: State, customer: any) {
  state.customerEntities = {
    ...state.customerEntities,
    [customer.id]: customer,
  }

  if (state.customerIds.indexOf(customer.id) < 0) {
    state.customerIds = [...state.customerIds, customer.id]
  }
}
